import React, { useCallback, useEffect, useRef, useState } from 'react'
import {
    Avatar,
    Button,
    Chip,
    CircularProgress,
    IconButton,
    Popover,
    TextField,
    Typography,
} from '@material-ui/core'
import { List as ListIcon, Refresh as RefreshIcon } from '@material-ui/icons'
import Autocomplete from '@material-ui/lab/Autocomplete'
import axios from 'axios'

import { ADMIN_SPID_GET, send_command } from 'components/api'
import { getApiErrorMessage } from 'utils/getApiErrorMessage'
import UserManagementDrawer from '../components/UserManagementDrawer'
import ConfirmActionDialog from '../components/ConfirmActionDialog'
import { EventStreamPanel } from '../components/EventStreamPanel'

// ─── Constants ───────────────────────────────────────────────────────────────

const ACCOUNT_NAMES = {
    '977807095132': 'Dev',
    '629078184297': 'Prod',
    '942866997229': 'Demo',
    '795187495424': 'DevRec',
    '340723061215': 'EU',
}

function healthcheckUrl(url) {
    if (!url) return ''
    const base = url.startsWith('http://') || url.startsWith('https://')
        ? url
        : `https://${url}`
    return `${base.replace(/\/$/, '')}/healthcheck`
}

function userInitials(user) {
    const g = (user?.given_name || '')[0] || ''
    const f = (user?.family_name || '')[0] || ''
    return (g + f).toUpperCase() || (user?.email || '?')[0].toUpperCase()
}

function uniqueCommandNames(result) {
    const items = Array.isArray(result) ? result : []
    const seen = new Set()
    const unique = []
    for (const item of items) {
        const name = (typeof item === 'string'
            ? item
            : item?.type || item?.command || ''
        ).trim().toUpperCase()
        if (name && !seen.has(name)) {
            seen.add(name)
            unique.push(name)
        }
    }
    return unique.sort()
}

// ─── Health Banner ────────────────────────────────────────────────────────────

function HealthBanner({ environment, loading, error, onRefresh }) {
    const variant = error ? 'error' : 'healthy'
    const label   = error ? 'Error'  : 'Healthy'

    return (
        <div className={`sa-health-banner sa-hb-${variant}`}>
            <span className="sa-hb-dot" />
            <span className="sa-hb-label">{label}</span>

            {!error && environment && <>
                <span className="sa-hb-sep" />
                <span className="sa-hb-kv">API <strong>{environment['api-version'] || '—'}</strong></span>
                <span className="sa-hb-sep" />
                <span className="sa-hb-kv">DB <strong>{environment['db-name'] || '—'}</strong></span>
                <span className="sa-hb-sep" />
                <span className="sa-hb-kv">Pool <strong>{environment['user-pool-name'] || '—'}</strong></span>
            </>}

            {error && <>
                <span className="sa-hb-sep" />
                <span className="sa-hb-kv" style={{ color: 'inherit' }}>{error}</span>
            </>}

            {loading && <CircularProgress size={13} style={{ marginLeft: 8, color: 'inherit' }} />}

            <span style={{ flex: 1 }} />
            <Button
                size="small"
                variant="outlined"
                onClick={onRefresh}
                startIcon={<RefreshIcon fontSize="small" />}
                style={{ fontSize: 11 }}
            >
                Refresh
            </Button>
        </div>
    )
}

// ─── Receiver Card ────────────────────────────────────────────────────────────

function ReceiverCard({ url }) {
    const [state, setState]       = useState({ status: 'untested' })
    const [checking, setChecking] = useState(false)
    const [expanded, setExpanded] = useState(false)

    const check = useCallback(() => {
        setChecking(true)
        axios.get(healthcheckUrl(url))
            .then(res => setState(res.data))
            .catch(() => setState({ status: 'fail' }))
            .finally(() => setChecking(false))
    }, [url])

    useEffect(() => { check() }, [check])

    const envLabel   = ACCOUNT_NAMES[state.accountId] || '?'
    const statusKey  = state.status === 'success' ? 'success'
                     : state.status === 'fail'    ? 'fail'
                     : 'untested'

    return (
        <div className={`sa-receiver-card sa-rc-${statusKey}`}>
            <div className="sa-rc-top">
                <Avatar className={`sa-rc-avatar sa-rc-av-${envLabel.toLowerCase()}`}>
                    {envLabel}
                </Avatar>
                <div className="sa-rc-info">
                    <div className="sa-rc-name">{state.region || envLabel}</div>
                    <div className="sa-rc-url">{url}</div>
                </div>
                <button className="sa-rc-btn" onClick={check} title="Re-check">
                    {checking
                        ? <CircularProgress size={12} />
                        : <RefreshIcon style={{ fontSize: 15 }} />}
                </button>
            </div>

            <div className="sa-rc-status-bar">
                <span className="sa-rc-status-pill">
                    <span className="sa-rc-dot" />
                    {statusKey.charAt(0).toUpperCase() + statusKey.slice(1)}
                </span>
            </div>

            {expanded && (
                <div className="sa-rc-details">
                    <div className="sa-rc-kv"><span className="sa-rc-k">Version</span> <span className="sa-rc-v">{state.version || '—'}</span></div>
                    <div className="sa-rc-kv"><span className="sa-rc-k">DBKey</span>   <span className="sa-rc-v">TenantID#{state.region || '—'}</span></div>
                    <div className="sa-rc-kv"><span className="sa-rc-k">Account</span> <span className="sa-rc-v">{state.accountId || '—'}</span></div>
                    <div className="sa-rc-kv"><span className="sa-rc-k">URL</span>     <span className="sa-rc-v">{url}</span></div>
                </div>
            )}

            <button className={`sa-rc-toggle ${expanded ? 'open' : ''}`} onClick={() => setExpanded(x => !x)}>
                <span>{expanded ? 'Hide' : 'Details'}</span>
                <span className="sa-rc-chevron">{expanded ? '▲' : '▼'}</span>
            </button>
        </div>
    )
}

// ─── Alerts Card ──────────────────────────────────────────────────────────────

function AlertsCard({ alerts }) {
    return (
        <div className="superadmin-card sa-alerts-card">
            <div className="sa-section-label">
                Alerts
                {alerts.length > 0 && (
                    <Chip
                        label={alerts.length}
                        size="small"
                        color="secondary"
                        style={{ marginLeft: 8, height: 18, fontSize: 10 }}
                    />
                )}
            </div>
            {alerts.length === 0 ? (
                <div className="sa-alert-none">
                    <span className="sa-alert-check">✓</span> No active alerts
                </div>
            ) : alerts.map((a, i) => (
                <div key={i} className={`sa-alert-item sa-alert-${a.level}`}>
                    <span className="sa-alert-icon">{a.level === 'error' ? '✕' : '▲'}</span>
                    <div>
                        <div className="sa-alert-source">{a.source}</div>
                        <div className="sa-alert-msg">{a.message}</div>
                    </div>
                </div>
            ))}
        </div>
    )
}


// ─── Popover result content ───────────────────────────────────────────────────

function PopError({ title, detail }) {
    return (
        <div className="sa-pop-error">
            <div className="sa-pop-error-title">{title}</div>
            {detail && <div className="sa-pop-error-detail">{detail}</div>}
        </div>
    )
}

function UserPopoverContent({ result, error, onOpenDrawer }) {
    if (error) return <PopError title="No user found" detail={error} />

    const isDisabled = !result.Enabled
    const isReset    = result.UserStatus === 'FORCE_CHANGE_PASSWORD'
                    || result.UserStatus === 'RESET_REQUIRED'
    let statusClass = 'status-active', statusLabel = 'Active'
    if (isDisabled)   { statusClass = 'status-disabled'; statusLabel = 'Disabled' }
    else if (isReset) { statusClass = 'status-reset';    statusLabel = 'Reset required' }

    const name = [result.given_name, result.family_name].filter(Boolean).join(' ') || result.email

    return (
        <div>
            <div style={{ display: 'flex', alignItems: 'center', gap: 12, marginBottom: 10 }}>
                <Avatar className={`superadmin-user-drawer drawer-avatar ${isDisabled ? 'disabled' : ''}`}
                    style={{ width: 40, height: 40, background: isDisabled ? '#8a8a8a' : '#03989e' }}>
                    {userInitials(result)}
                </Avatar>
                <div>
                    <div style={{ fontWeight: 600, fontSize: 14 }}>{name}</div>
                    <div style={{ fontSize: 12, color: '#8a8a8a' }}>{result.email}</div>
                    {result.tenant_name && (
                        <div style={{ fontSize: 12, color: '#8a8a8a' }}>{result.tenant_name}</div>
                    )}
                </div>
            </div>
            <span className={`superadmin-status-badge ${statusClass}`}>
                <span className="dot" />{statusLabel}
            </span>
            <div style={{ marginTop: 14, textAlign: 'right' }}>
                <Button size="small" variant="outlined" onClick={onOpenDrawer}>
                    Open profile
                </Button>
            </div>
        </div>
    )
}

function SpidPopoverContent({ result, error }) {
    if (error) return <PopError title="SPID not found" detail={error} />

    const rows = [
        ['Subject', result.subject_name || result.name   || '—'],
        ['Email',   result.email                         || '—'],
        ['Tenant',  result.tenant_name || result.tenant_id || '—'],
        ['SPID',    result.sp_id       || result.spid    || '—'],
        ['Status',  result.status                        || 'Active'],
    ]

    return (
        <div className="sa-pop-kv-list">
            {rows.map(([k, v]) => (
                <div key={k} className="sa-pop-kv">
                    <span className="sa-pop-k">{k}</span>
                    <span className="sa-pop-v">{v}</span>
                </div>
            ))}
        </div>
    )
}

function CmdPopoverContent({ result, error, onCopy }) {
    if (error) return <PopError title="Command failed" detail={error} />
    return (
        <div>
            <pre className="sa-pop-cmd-pre">{JSON.stringify(result, null, 2)}</pre>
            <div style={{ marginTop: 8, textAlign: 'right' }}>
                <Button size="small" onClick={onCopy}>Copy</Button>
            </div>
        </div>
    )
}

// ─── Quick Action Tools ───────────────────────────────────────────────────────

function UserLookupTool({ onResult }) {
    const [email, setEmail]     = useState('')
    const [loading, setLoading] = useState(false)
    const anchorRef = useRef()

    const lookup = () => {
        const q = email.trim().toLowerCase()
        if (!q) return
        setLoading(true)
        send_command('ADMIN_LIST_ALL_USERS', {})
            .then(users => {
                const found = (Array.isArray(users) ? users : [])
                    .find(u => (u.email || '').toLowerCase() === q)
                if (found) {
                    onResult(anchorRef.current, 'user', { result: found, error: null })
                } else {
                    onResult(anchorRef.current, 'user', {
                        result: null,
                        error: `No account matches "${email.trim()}"`,
                    })
                }
            })
            .catch(err => onResult(anchorRef.current, 'user', {
                result: null,
                error: getApiErrorMessage(err, 'Lookup failed'),
            }))
            .finally(() => setLoading(false))
    }

    return (
        <div className="sa-tool-card" ref={anchorRef}>
            <div className="sa-tool-header">
                <div className="sa-tool-name">User Lookup</div>
                <div className="sa-tool-desc">Find a user by email across all tenants</div>
            </div>
            <div className="sa-tool-body">
                <div style={{ display: 'flex', gap: 8 }}>
                    <TextField
                        value={email}
                        onChange={e => setEmail(e.target.value)}
                        onKeyDown={e => e.key === 'Enter' && lookup()}
                        placeholder="email@example.com"
                        variant="outlined"
                        size="small"
                        type="email"
                        style={{ flex: 1 }}
                    />
                    <Button
                        variant="contained"
                        color="primary"
                        size="small"
                        onClick={lookup}
                        disabled={loading || !email.trim()}
                    >
                        {loading ? <CircularProgress size={14} /> : 'Look up'}
                    </Button>
                </div>
            </div>
        </div>
    )
}

function SpidLookupTool({ onResult }) {
    const [spid, setSpid]       = useState('')
    const [loading, setLoading] = useState(false)
    const anchorRef = useRef()

    const lookup = () => {
        const q = spid.trim()
        if (!q) return
        setLoading(true)
        ADMIN_SPID_GET(q)
            .then(result => onResult(anchorRef.current, 'spid', { result, error: null }))
            .catch(err   => onResult(anchorRef.current, 'spid', {
                result: null,
                error: getApiErrorMessage(err, `No record found for "${q}"`),
            }))
            .finally(() => setLoading(false))
    }

    return (
        <div className="sa-tool-card" ref={anchorRef}>
            <div className="sa-tool-header">
                <div className="sa-tool-name">SPID Lookup</div>
                <div className="sa-tool-desc">Resolve a subject ID to a user record</div>
            </div>
            <div className="sa-tool-body">
                <div style={{ display: 'flex', gap: 8 }}>
                    <TextField
                        value={spid}
                        onChange={e => setSpid(e.target.value)}
                        onKeyDown={e => e.key === 'Enter' && lookup()}
                        placeholder="spid_abc123…"
                        variant="outlined"
                        size="small"
                        inputProps={{ style: { fontFamily: 'monospace' } }}
                        style={{ flex: 1 }}
                    />
                    <Button
                        variant="contained"
                        color="primary"
                        size="small"
                        onClick={lookup}
                        disabled={loading || !spid.trim()}
                    >
                        {loading ? <CircularProgress size={14} /> : 'Look up'}
                    </Button>
                </div>
            </div>
        </div>
    )
}

function CommandTool({ onResult }) {
    const [cmdName, setCmdName]     = useState('')
    const [payload, setPayload]     = useState('{}')
    const [parseError, setParseError] = useState('')
    const [loading, setLoading]     = useState(false)
    const [commands, setCommands]   = useState([])
    const [commandsLoading, setCommandsLoading] = useState(true)
    const [commandsError, setCommandsError] = useState(null)
    const [listAnchor, setListAnchor] = useState(null)
    const anchorRef = useRef()

    const loadCommands = useCallback(() => {
        setCommandsLoading(true)
        send_command('LIST_COMMANDS', {})
            .then(res => {
                setCommands(uniqueCommandNames(res))
                setCommandsError(null)
            })
            .catch(err => {
                setCommands([])
                setCommandsError(getApiErrorMessage(err, 'Failed to load commands'))
            })
            .finally(() => setCommandsLoading(false))
    }, [])

    useEffect(() => { loadCommands() }, [loadCommands])

    const validatePayload = val => {
        try { JSON.parse(val); setParseError(''); return true }
        catch (e) { setParseError(e.message); return false }
    }

    const send = () => {
        const name = cmdName.trim().toUpperCase()
        if (!name) { setParseError('Command name is required'); return }
        if (!validatePayload(payload)) return
        setLoading(true)
        send_command(name, JSON.parse(payload))
            .then(result => onResult(anchorRef.current, 'cmd', { result, error: null, cmdName: name }))
            .catch(err   => onResult(anchorRef.current, 'cmd', {
                result: null,
                error: getApiErrorMessage(err, 'Command failed'),
                cmdName: name,
            }))
            .finally(() => setLoading(false))
    }

    const commandDesc = commandsLoading
        ? 'Loading available commands…'
        : commands.length > 0
            ? `${commands.length} commands available — pick from the list or type your own`
            : 'Send a backend admin command with JSON payload'

    return (
        <div className="sa-tool-card" ref={anchorRef}>
            <div className="sa-tool-header">
                <div className="sa-tool-name">Issue Command</div>
                <div className="sa-tool-desc">{commandDesc}</div>
            </div>
            <div className="sa-tool-body">
                <div className="sa-cmd-input-row">
                    <Autocomplete
                        freeSolo
                        options={commands}
                        value={cmdName}
                        onChange={(_e, newValue) => {
                            setCmdName((newValue || '').toUpperCase())
                            setParseError('')
                        }}
                        onInputChange={(_e, newInputValue) => {
                            setCmdName((newInputValue || '').toUpperCase())
                            setParseError('')
                        }}
                        loading={commandsLoading}
                        disabled={loading}
                        renderInput={params => (
                            <TextField
                                {...params}
                                label="Command"
                                variant="outlined"
                                size="small"
                                fullWidth
                                placeholder={commands.length ? 'Select a command' : 'LIST_COMMANDS'}
                                onKeyDown={e => e.key === 'Enter' && send()}
                                inputProps={{
                                    ...params.inputProps,
                                    style: { fontFamily: 'monospace' },
                                }}
                                InputProps={{
                                    ...params.InputProps,
                                    endAdornment: (
                                        <>
                                            {commandsLoading ? <CircularProgress size={14} /> : null}
                                            {params.InputProps.endAdornment}
                                        </>
                                    ),
                                }}
                            />
                        )}
                    />
                    <IconButton
                        size="small"
                        className="sa-cmd-list-btn"
                        title="Browse available commands"
                        aria-label="Browse available commands"
                        disabled={loading}
                        onClick={e => setListAnchor(e.currentTarget)}
                    >
                        <ListIcon fontSize="small" />
                    </IconButton>
                </div>
                <Popover
                    open={Boolean(listAnchor)}
                    anchorEl={listAnchor}
                    onClose={() => setListAnchor(null)}
                    anchorOrigin={{ vertical: 'bottom', horizontal: 'right' }}
                    transformOrigin={{ vertical: 'top', horizontal: 'right' }}
                    PaperProps={{ className: 'sa-cmd-list-popover' }}
                >
                    <div className="sa-pop-header">
                        <span className="sa-pop-title">Available Commands</span>
                        <button
                            type="button"
                            className="sa-pop-close"
                            onClick={() => setListAnchor(null)}
                        >
                            ✕
                        </button>
                    </div>
                    <div className="sa-pop-body sa-cmd-list-body">
                        {commandsLoading ? (
                            <div className="sa-cmd-list-status">
                                <CircularProgress size={16} />
                                <span>Loading commands…</span>
                            </div>
                        ) : commandsError ? (
                            <PopError title="Failed to load commands" detail={commandsError} />
                        ) : commands.length === 0 ? (
                            <Typography variant="body2" color="textSecondary">
                                No commands returned.
                            </Typography>
                        ) : (
                            <ul className="sa-cmd-list">
                                {commands.map(name => (
                                    <li key={name}>
                                        <button
                                            type="button"
                                            className={`sa-cmd-list-item${cmdName === name ? ' selected' : ''}`}
                                            onClick={() => {
                                                setCmdName(name)
                                                setParseError('')
                                                setListAnchor(null)
                                            }}
                                        >
                                            {name}
                                        </button>
                                    </li>
                                ))}
                            </ul>
                        )}
                    </div>
                </Popover>
                {commandsError && (
                    <Typography variant="caption" color="error" display="block" style={{ marginBottom: 6 }}>
                        {commandsError}{' '}
                        <button
                            type="button"
                            onClick={loadCommands}
                            style={{
                                background: 'none',
                                border: 'none',
                                padding: 0,
                                color: 'inherit',
                                textDecoration: 'underline',
                                cursor: 'pointer',
                                font: 'inherit',
                            }}
                        >
                            Retry
                        </button>
                    </Typography>
                )}
                <TextField
                    value={payload}
                    onChange={e => { setPayload(e.target.value); validatePayload(e.target.value) }}
                    placeholder="{}"
                    variant="outlined"
                    size="small"
                    fullWidth
                    multiline
                    rows={2}
                    label="Payload (JSON)"
                    inputProps={{ style: { fontFamily: 'monospace', fontSize: 12 } }}
                    style={{ marginBottom: 8 }}
                />
                {parseError && (
                    <Typography variant="caption" color="error" display="block" style={{ marginBottom: 6 }}>
                        ⚠ {parseError}
                    </Typography>
                )}
                <Button
                    variant="contained"
                    color="primary"
                    size="small"
                    fullWidth
                    onClick={send}
                    disabled={loading}
                >
                    {loading ? <CircularProgress size={14} /> : 'Send Command'}
                </Button>
            </div>
        </div>
    )
}

// ─── SystemSection ────────────────────────────────────────────────────────────

export function SystemSection() {
    const confirmRef = useRef()

    const [environment,    setEnvironment]    = useState(null)
    const [envLoading,     setEnvLoading]     = useState(true)
    const [envError,       setEnvError]       = useState(null)

    const [receiverUrls,      setReceiverUrls]      = useState([])
    const [receiversLoading,  setReceiversLoading]  = useState(true)
    const [receiversError,    setReceiversError]    = useState(null)


    // Popover
    const [popAnchor,  setPopAnchor]  = useState(null)
    const [popState,   setPopState]   = useState(null) // { type, result, error, cmdName }

    // User management drawer (opened from user lookup result)
    const [drawerUser, setDrawerUser] = useState(null)

    const loadEnvironment = useCallback(() => {
        setEnvLoading(true)
        send_command('ADMIN_GET_ENVIRONMENT_DETAILS', {})
            .then(res => { setEnvironment(res); setEnvError(null) })
            .catch(err => setEnvError(getApiErrorMessage(err, 'Failed to load environment details')))
            .finally(() => setEnvLoading(false))
    }, [])

    const loadReceivers = useCallback(() => {
        setReceiversLoading(true)
        send_command('ADMIN_GET_RECEIVERS', {})
            .then(res => { setReceiverUrls(Array.isArray(res) ? res : []); setReceiversError(null) })
            .catch(err => setReceiversError(getApiErrorMessage(err, 'Failed to load receivers')))
            .finally(() => setReceiversLoading(false))
    }, [])

    const refreshAll = useCallback(() => {
        loadEnvironment()
        loadReceivers()
    }, [loadEnvironment, loadReceivers])

    useEffect(() => { refreshAll() }, [refreshAll])

    // Aggregate page-level alerts
    const alerts = [
        envError       && { level: 'error', source: 'Environment API', message: envError },
        receiversError && { level: 'warn',  source: 'Receivers',       message: receiversError },
    ].filter(Boolean)

    const handleToolResult = useCallback((anchor, type, state) => {
        setPopState({ type, ...state })
        setPopAnchor(anchor)
    }, [])

    const closePopover = () => setPopAnchor(null)

    // Build popover title + body from current popState
    let popTitle   = ''
    let popContent = null
    if (popState) {
        if (popState.type === 'user') {
            popTitle   = 'User Lookup'
            popContent = (
                <UserPopoverContent
                    result={popState.result}
                    error={popState.error}
                    onOpenDrawer={() => { setDrawerUser(popState.result); closePopover() }}
                />
            )
        } else if (popState.type === 'spid') {
            popTitle   = 'SPID Lookup'
            popContent = <SpidPopoverContent result={popState.result} error={popState.error} />
        } else if (popState.type === 'cmd') {
            popTitle   = `Response: ${popState.cmdName || 'Command'}`
            popContent = (
                <CmdPopoverContent
                    result={popState.result}
                    error={popState.error}
                    onCopy={() => {
                        if (popState.result) {
                            navigator.clipboard.writeText(
                                JSON.stringify(popState.result, null, 2)
                            ).catch(() => {})
                        }
                    }}
                />
            )
        }
    }

    return (
        <>
            <Typography variant="h6" gutterBottom>System Overview</Typography>

            {/* 1 — Health banner */}
            <HealthBanner
                environment={environment}
                loading={envLoading}
                error={envError}
                onRefresh={refreshAll}
            />

            {/* 2 — Receiver cards */}
            {receiversLoading ? (
                <div style={{ marginBottom: 16 }}><CircularProgress size={20} /></div>
            ) : receiverUrls.length > 0 && (
                <div className="sa-receiver-row">
                    {receiverUrls.map(url => <ReceiverCard key={url} url={url} />)}
                </div>
            )}

            {/* 3 — Alerts */}
            <AlertsCard alerts={alerts} />

            {/* 4 — Quick Actions */}
            <div className="sa-section-label" style={{ marginTop: 24, marginBottom: 8 }}>
                Quick Actions
            </div>
            <div className="sa-tools-grid">
                <UserLookupTool onResult={handleToolResult} />
                <SpidLookupTool onResult={handleToolResult} />
                <CommandTool    onResult={handleToolResult} />
            </div>

            <div className="superadmin-card sa-event-stream-card">
                <div className="sa-section-label" style={{ marginTop: 8, marginBottom: 8 }}>
                    Recent events (all tenants)
                </div>
                <EventStreamPanel
                    showTenantColumn
                    defaultKinds={[
                        'integration_started',
                        'integration_completed',
                        'integration_failed',
                        'client_error',
                        'client_event',
                        'heartbeat',
                        'exception',
                    ]}
                    hint="System-wide feed of client integration events, heartbeats, and platform exceptions."
                />
            </div>

            {/* Result popover */}
            <Popover
                open={Boolean(popAnchor)}
                anchorEl={popAnchor}
                onClose={closePopover}
                anchorOrigin={{ vertical: 'bottom', horizontal: 'left' }}
                transformOrigin={{ vertical: 'top', horizontal: 'left' }}
                PaperProps={{ className: 'sa-result-popover' }}
            >
                <div className="sa-pop-header">
                    <span className="sa-pop-title">{popTitle}</span>
                    <button className="sa-pop-close" onClick={closePopover}>✕</button>
                </div>
                <div className="sa-pop-body">{popContent}</div>
            </Popover>

            {/* User management drawer — opened from lookup result */}
            <UserManagementDrawer
                user={drawerUser}
                open={!!drawerUser}
                onClose={() => setDrawerUser(null)}
                onChanged={() => {}}
                onToast={() => {}}
                onError={() => {}}
                confirmRef={confirmRef}
            />
            <ConfirmActionDialog ref={confirmRef} />
        </>
    )
}
